import { CurrencyPipe, isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, Subscription } from 'rxjs';

import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { BookingService } from '~/checkout/services/booking/booking.service';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import { CtaVariantEnum } from '~/component/call-to-action/enums/variant.enum';
import { CtaWidthEnum } from '~/component/call-to-action/enums/width.enum';
import {
  getCountryFromLocale,
  getLangFromLocale,
} from '~/core/helpers/locale/locale.helper';
import { toDecimalPrice, toIntegerPrice } from '~/core/helpers/numbers.helper';
import { DisplayPricePipe } from '~/core/pipes/display-price.pipe';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { WindowService } from '~/core/services/window/window.service';
import { LoaderService } from '~/core/services/loader/loader.service';

import { environment } from 'src/environments/environment';
import { CreateSessionResponse, BookingStatus } from '@sport-activities/sdk';
import { map, mergeMap } from 'rxjs/operators';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { APP_STORAGE } from '~/app.token';
import { OnePayConfigInterface } from '~/checkout/interfaces/onepay-config.interface';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { PromoCodeService } from '~/payment/services/promocode/promocode.service';
import { PromoCodeInterface } from '../promo-code/payment-promo-code.interface';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { ModalComponent } from '~/component/modal/modal.component';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { CtaButtonTypeEnum } from '~/component/call-to-action/enums/button-type.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { RedirectionService } from '~/core/services/redirection/redirection.service';
import { CheckboxAlignEnum } from '~/component/form/checkbox/enums/checkbox-align.enum';
import { legalTermsUsageServiceUrlToggle } from '~/toggles/default-values/legal/terms-usage-service-url';

declare global {
  interface Window {
    payment: {
      init: (config: OnePayConfigInterface) => void;
      reload: (config: OnePayConfigInterface) => void;
      processPayment: () => void;
    };
  }
}

export interface BmsTermsInterface {
  name: string;
  url: string;
  legalName: string;
  requiresExplicitConsent: boolean;
  type: string;
}

export interface BmsBookingInitResponseInterface {
  bookingId: string;
  status: BookingStatus;
  onePaySession?: CreateSessionResponse;
  bmsTerms?: BmsTermsInterface[];
}

@Component({
  selector: 'app-checkout-payment-onepay',
  templateUrl: './payment-onepay.component.html',
  styleUrls: ['./payment-onepay.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CheckoutPaymentOnePayComponent implements OnInit, OnDestroy {
  @ViewChild('bookingErrorModal') public bookingErrorModal: ModalComponent;

  @Output() public submitPromoCode = new EventEmitter<void>();

  @Input() public items: CheckoutItem[];
  @Input() public promoCode?: PromoCodeInterface;
  @Input() public discountValue?: number;

  private readonly subscriptions = new Subscription();

  public readonly toggleNameEnum = ToggleNameEnum;
  public readonly checkboxAlign = CheckboxAlignEnum.Top;
  public readonly ctaVariantEnum = CtaVariantEnum;
  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly submitButtonWidth = CtaWidthEnum.Responsive;
  public readonly ctaButtonType = CtaButtonTypeEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  public bmsTerms: BmsTermsInterface[];
  public legalTermsText = '';
  public isBmsConsentMandatory: boolean;
  public errorText: string;
  public bookingId: string;
  public consent = false;
  public isImageRightsConsented = false;
  public totalPriceWithCurrency: string;
  public isMobileDevice: boolean;
  public error?: string;
  public isLoading: boolean;
  public showPaymentForm = false;
  public canCheckout = false;
  public freeCheckout = false;
  public onepayScripts = [
    `${environment.applications.onepay.host}/configuration.js`,
    `${environment.applications.onepay.host}/message_default.js`,
    `${environment.applications.onepay.host}/payment.js`,
  ];

  constructor(
    private readonly renderer: Renderer2,
    public readonly elementRef: ElementRef,
    private readonly checkoutService: CheckoutService,
    private readonly windowService: WindowService,
    private readonly bookingService: BookingService,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly matchMediaService: MatchMediaService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly toggleService: ToggleService,
    private readonly translateService: TranslateService,
    private readonly loaderService: LoaderService,
    private readonly localizeRouter: LocalizeRouterService,
    private readonly promoCodeService: PromoCodeService,
    private readonly redirectionService: RedirectionService,
    @Inject(APP_STORAGE) protected readonly storage: Storage
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  public ngOnInit(): void {
    // Manually subscribe to loaderService instead of using "loading" directive
    // to avoid conflicts on updating "disabled" button property
    this.subscriptions.add(
      this.loaderService.loading$.subscribe((loader) => {
        this.isLoading = loader.state.loading;
      })
    );

    // check window size
    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );

    if (this.isPromoCodeEnabled) {
      this.subscriptions.add(
        this.promoCodeService
          .getPromoCodeValue()
          .subscribe((promoCodeResponse) => {
            this.canCheckout = this.freeCheckout =
              promoCodeResponse?.priceFinal === 0;
            if (this.promoCode !== promoCodeResponse) {
              this.promoCode = promoCodeResponse;
              this.loadOnePay();
              this.getTotalPriceWithCurrency(promoCodeResponse);
            }
          })
      );
    }

    // no need to inject onepay widget in SSR
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.isPromoCodeEnabled) {
      this.subscriptions.add(this.loadOnePay());
      this.getTotalPriceWithCurrency();
    }
  }

  public get creditCardTrans(): string {
    return this.translateService.instant('onepay.credit_card');
  }

  public get isBms(): boolean {
    return this.bmsTerms?.length > 0;
  }

  public get isLegalTermsUsageLinkDisplayEnabled(): boolean {
    return this.toggleService.isEnabled(
      this.toggleNameEnum.LegalTermsUsageLinkDisplay
    );
  }

  public get isPreBookingEnabled(): boolean {
    return this.toggleService.isEnabled(this.toggleNameEnum.PreBooking);
  }

  public get isPromoCodeEnabled(): boolean {
    return this.toggleService.isEnabled(this.toggleNameEnum.PromoCode);
  }

  public legalTermsTranslation(): string {
    if (this.isBms) {
      const translation = this.checkoutService.getBmsTermsTrans(
        this.bmsTerms,
        this.isBmsConsentMandatory
      );
      this.legalTermsText = translation;
      return translation;
    }

    if (!this.isBms && this.isLegalTermsUsageLinkDisplayEnabled) {
      const translation = this.translateService.instant(
        'checkout.summary.conditions.free',
        { termsOfUse: this.legalTermsUsageServiceUrl }
      );
      this.legalTermsText = translation;
      return translation;
    }

    const translation = this.translateService.instant(
      'checkout.summary.conditions_with_links',
      {
        legalTermsUrl: this.legalTermsUsageServiceUrl || '',
        privacyPolicyUrl: this.privacyPolicyUrl || '',
      }
    );
    this.legalTermsText = translation;
    return translation;
  }

  public get legalTermsSalesUrl(): object | string {
    return this.toggleService.getVariant(
      ToggleNameEnum.LegalTermsSalesUrl,
      legalTermsUsageServiceUrlToggle
    );
  }

  public get legalTermsUsageServiceUrl(): object | string {
    return this.toggleService.getVariant(
      ToggleNameEnum.LegalTermsUsageParticipantUrl,
      legalTermsUsageServiceUrlToggle
    );
  }

  public get linkIcon(): VtmnPlayIconTypeEnum {
    return VtmnPlayIconTypeEnum.Link;
  }

  public get onepay(): typeof global.Window.prototype.payment {
    return this.windowService.nativeWindow.payment;
  }

  public get paidBooking(): boolean {
    return !this.promoCode || this.promoCode?.priceFinal > 0;
  }

  public get paymentDisabled(): boolean {
    return !this.consent || !this.canCheckout || this.isLoading ? true : null;
  }

  public get privacyPolicyUrl(): object | string {
    return this.toggleService.getVariantString(
      ToggleNameEnum.PrivacyPolicyUrl,
      ''
    );
  }

  public clearPromoCode(): void {
    this.submitPromoCode.emit(null);
  }

  public loadOnePay(): void {
    this.showPaymentForm = false;
    this.loadOnepayScript()
      .pipe(mergeMap(() => this.initBooking()))
      .subscribe((booking) => {
        if (booking?.bmsTerms) {
          this.bmsTerms = booking?.bmsTerms;
        }

        if (this.isPreBookingEnabled) {
          this.bookingId = booking?.bookingId;
        }

        // init widget
        this.onepay.reload(this.getOnepayConfig(booking.onePaySession));

        this.isBmsConsentMandatory = this.bmsTerms?.some(
          (item) => item?.requiresExplicitConsent === true
        );

        if (this.isBms && !this.isBmsConsentMandatory) {
          this.consent = true;
        }

        // show payment form
        this.showPaymentForm = true;

        this.legalTermsTranslation();
      });
  }

  public onWidgetError(msg: string) {
    this.loaderService.stop();
    this.error = msg;
    if (this.isPreBookingEnabled) {
      this.subscriptions.add(
        this.bookingService.deleteOneFFBooking(this.bookingId).subscribe()
      );
    }
    this.loadOnePay();
  }

  private initBooking(): Observable<BmsBookingInitResponseInterface> {
    // Retrieve original cart if promocode deleted
    if (this.isPromoCodeEnabled && !this.promoCode) {
      this.items = this.checkoutService.getCartContent();
      this.getTotalPriceWithCurrency();
    }

    const redirection = `${
      environment.applications.front.url
    }${this.localizeRouter.translateRoute('/attendee.homepage').toString()}`.slice(
      0,
      -1
    );

    return this.bookingService.init(
      this.items,
      redirection,
      this.isPromoCodeEnabled && this.promoCode !== undefined
        ? this.promoCode
        : null
    );
  }

  private getOnepayConfig(
    session: CreateSessionResponse
  ): OnePayConfigInterface {
    const { apiKey, entityId, apiBaseUrl } = environment.applications.onepay;

    return {
      config: {
        payment_base_url: apiBaseUrl,
        entity_id: entityId,
        merchant_id: 'decathlon-activities',
        use_css: true,
        notifiers: {
          success: () => {
            this.loaderService.stop();
          },
          error: (msg: string) => {
            this.onWidgetError(msg);
          },
          payability: (payability) => {
            this.canCheckout = payability || this.promoCode?.priceFinal === 0;
          },
          preSubmit: () =>
            this.bookingService
              .patchBooking(this.bookingId, this.isImageRightsConsented)
              .subscribe({
                error: (err) => {
                  console.log('Error on Image right update :: ', err);
                },
              }),
        },
        locale: {
          country: getCountryFromLocale(this.translateService.currentLang),
          language: getLangFromLocale(this.translateService.currentLang),
        },
        currency: this.items[0]?.currency,
        key: apiKey,
      },
      session,
    };
  }

  // total price (displayed in the fixed panel in mobile)
  public getTotalPriceWithCurrency(
    promoCodeResponse?: PromoCodeInterface
  ): string {
    if (!this.items && !this.items?.length) {
      return '';
    }

    const displayPricePipe = new DisplayPricePipe(
      this.currencyPipe,
      this.translateService
    );

    const currency = this.items[0]?.currency;
    let totalPrice = 0;

    const getAmount = () => {
      return new Promise((resolve) => {
        if (this.isPromoCodeEnabled && promoCodeResponse) {
          this.promoCode = promoCodeResponse;
          this.discountValue = promoCodeResponse.discountAmount;
          totalPrice = toIntegerPrice(promoCodeResponse.priceFinal);
          resolve(totalPrice);
        }

        this.items.forEach((checkoutItem: CheckoutItem) => {
          totalPrice += toIntegerPrice(checkoutItem.totalPrice);
        });
        resolve(totalPrice);
      });
    };

    getAmount().then((totalPrice) => {
      this.totalPriceWithCurrency = displayPricePipe.transform(
        toDecimalPrice(Number(totalPrice)),
        currency
      );
      return this.totalPriceWithCurrency;
    });
  }

  // load mandatory onepay script
  private loadOnepayScript(): Observable<string[]> {
    return forkJoin(
      this.onepayScripts.map((src) => {
        // create an observable for every script
        return new Observable<string>((subscriber) => {
          const script = this.renderer.createElement('script');
          script.src = src;

          script.onload = () => {
            // complete observable after script load
            subscriber.next(src);
            subscriber.complete();
          };

          this.renderer.appendChild(this.elementRef.nativeElement, script);
        });
      })
    );
  }

  public get labelButtonTranslation(): string {
    return this.promoCode?.priceFinal === 0
      ? this.translateService.instant(
          'checkout.payment.form.submit_button_free'
        )
      : this.translateService.instant('checkout.payment.form.submit_button', {
          total: this.totalPriceWithCurrency,
        });
  }

  public get buttonSize(): string {
    return this.isMobileDevice
      ? this.vtmnPlayButtonSize.MEDIUM
      : this.vtmnPlayButtonSize.LARGE;
  }

  public finishBooking(): void {
    this.loaderService.start();

    if (this.isPromoCodeEnabled && this.promoCode?.priceFinal === 0) {
      // if free booking
      this.bookingService
        .start(
          this.items,
          this.isPromoCodeEnabled && this.promoCode !== undefined
            ? this.promoCode
            : null
        )
        .pipe(
          mergeMap((bookingResponse) => {
            return this.bookingService.confirm(bookingResponse.bookingId);
          }),
          map(() => {
            this.loaderService.stop();
            this.checkoutService.emptyCart();
            this.promoCodeService.clearPromoCode();
            return this.checkoutService.redirectToConfirmation();
          })
        )
        .subscribe();
    }
    // else process payment
    if (this.isPromoCodeEnabled) {
      this.promoCodeService.clearPromoCode();
    }

    if (!this.isPreBookingEnabled && this.paidBooking) {
      this.error = null;
      return this.onepay.processPayment();
    }
    // else prebooking enabled
    if (this.isPreBookingEnabled && this.paidBooking) {
      const preBook = () => {
        return new Promise<void>((resolve, reject) => {
          this.subscriptions.add(
            this.bookingService.addOneFFBooking(this.bookingId).subscribe({
              complete: () => {
                resolve();
              },
              error: (error) => {
                this.errorText = this.translateService.instant(
                  `checkout.payment.oneff.error.${error.status}`
                );
                this.openBookingErrorModal();
                reject(new Error(error));
              },
            })
          );
        });
      };

      preBook().then(() => {
        this.error = null;
        return this.onepay.processPayment();
      });
    }
  }

  public openBookingErrorModal(): void {
    this.bookingErrorModal.show();
  }

  public closeBookingErrorModal(): void {
    this.bookingErrorModal.hide();
  }
}
